import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounce } from 'use-debounce';
import dayjs from 'dayjs';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid
} from '@mui/material';

import { Chart, CloseButton, MDBox, MDButton, MDTypography } from '@components';

import { RootState } from '@store/store';
import { setPlanUpdateErrors } from '@store/AppSlice';
import { setUserNotifications } from '@components/Notification/slice';

import { containsWhiteSpace, getPlanName, saveDomAsImage } from '@utils/common';
import { sendAmplitudeData } from '@utils/amplitude';
import { sendGAData } from '@utils/googleAnalytics';

import breakpoints from '@theme/base/breakpoints';
import { NotificationData, MSG_EVENT, MSG_TYPE } from '@DStypes/notifications';
import {
  DATE_GROUP,
  DATE_TIME_GROUP,
  numberEquivalents
} from '@constants/common';
import {
  CLICKED_REMOVE_WATERMARK,
  DOWNLOAD_CHART
} from '@constants/eventNames';

interface DownloadPreviewModalProps {
  open: boolean;
  onClose: () => void;
  chartTitle: string;
  chartOperation: string;
  chartShortDescription?: string;
  chartDescription?: string;
  chartData: any[];
  chartConfig: any;
  chartId: any;
  chartType: any;
  visualSettings: any;
  colorSet: string[] | null;
  projectName?: string;
  dateGroup?: DATE_GROUP | 'none' | '' | DATE_TIME_GROUP;
  projectId?: string;
  isSegmented: boolean;
  brand?: string;
  imageBrand?: boolean;
  isDate?: boolean;
  chartConfigData: any;
}

const DownloadPreviewModal: FC<DownloadPreviewModalProps> = ({
  open,
  onClose,
  chartTitle,
  chartDescription,
  chartOperation,
  chartData,
  chartConfig,
  chartId,
  chartType,
  visualSettings,
  colorSet,
  projectName = '',
  dateGroup = '',
  projectId = '',
  isSegmented,
  isDate,
  chartConfigData
}) => {
  const dispatch = useDispatch();

  const userDetails = useSelector((state: RootState) => state?.userDetails);
  const subscriptionFlag = useSelector(
    (state: RootState) => state?.AppData?.subKeyFlag
  );
  const isRemoveWaterMarkEnabledByPlan = useSelector(
    (state: RootState) => state?.AppData?.currentPlanFeatures?.removeWaterMark
  );

  const exportName = chartTitle as string;
  const editChartHeight =
    document.getElementById('chartContainer')?.clientHeight;
  const editChartWidth =
    document.getElementById('editChartContainer')?.clientWidth;
  const rootWidth = document.getElementById('root')?.clientWidth || 1200;

  const domRef = useRef<HTMLElement>();
  const domChartRef = useRef<HTMLElement>();
  const [mobileView, setMobileView] = useState<boolean>(false);
  const [isChartLoading, setIsChartLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  const [debouncedWindowWidth] = useDebounce(window.innerWidth, 2000);

  const getChart = useMemo<boolean | JSX.Element>(() => {
    const selfChart =
      chartConfigData?.config?.x_axis.id ===
      chartConfigData?.config?.y_axis?.id;

    const isNumber = numberEquivalents.includes(
      chartConfigData?.config?.x_axis?.treat_as_type
    );

    const isYPercentage =
      chartConfigData?.config?.y_axis?.treat_as_type === 'PERCENTAGE'
        ? true
        : false;

    const isXPercentage =
      chartConfigData?.config?.x_axis?.treat_as_type === 'PERCENTAGE'
        ? true
        : false;

    const xAxisField = chartConfigData?.config?.x_axis?.fieldname || '';

    let xDateItemsToRender = 55;

    if (window.innerWidth > 1550) xDateItemsToRender = 45;
    else if (window.innerWidth > 1024) xDateItemsToRender = 30;
    else if (window.innerWidth > 768) xDateItemsToRender = 20;
    else if (window.innerWidth > 400) xDateItemsToRender = 13;
    else xDateItemsToRender = 12;

    return (
      <Chart
        chartViewRef={domChartRef}
        chartId={chartId}
        data={chartData}
        chartConfig={{
          ...chartConfig,
          legend: { ...chartConfig.legend, scrollable: false }
        }}
        chartType={chartType}
        height={editChartHeight ? editChartHeight - 20 + 'px' : '65vh'}
        width={
          editChartWidth ? editChartWidth - 20 + 'px' : `${rootWidth - 240}px`
        }
        visualSettings={visualSettings}
        isSegmented={isSegmented}
        isPreview
        colorSet={colorSet}
        dateGroup={dateGroup}
        isDate={isDate}
        xDateItemsToRender={xDateItemsToRender}
        isSelfChart={selfChart}
        isNumber={isNumber}
        chartOperation={chartOperation}
        title={chartTitle}
        handleOnChartReady={() => setIsChartLoading(false)}
        isYPercentage={isYPercentage}
        isXPercentage={isXPercentage}
        showWaterMark={subscriptionFlag && !isRemoveWaterMarkEnabledByPlan}
        onClickRemoveWaterMark={() => {
          if (subscriptionFlag) {
            sendAmplitudeData(CLICKED_REMOVE_WATERMARK, {
              location_type: 'Download Chart'
            });
            dispatch(
              setPlanUpdateErrors({
                button_text: 'Choose a plan',

                feature_name: 'Remove watermark',
                modal_name: 'Feature Limit',
                title: `Access this feature with an upgrade to your current ${getPlanName(
                  userDetails.plan,
                  userDetails.is_trial
                )} plan.`,
                sub_title:
                  'Upgrade your plan to enjoy using advanced features. Simply click below and choose from a wide range of plans that suit your needs.'
              })
            );
          }
        }}
        skipEmptyPeriods={chartConfigData?.config?.date_group === 'none'}
        xAxisField={xAxisField}
      />
    );
  }, [open, chartData, chartConfig, chartConfigData?.config?.date_group]);

  useEffect(() => {
    if (debouncedWindowWidth < breakpoints.values.lg) setMobileView(true);
    else setMobileView(false);
  }, [debouncedWindowWidth]);

  const handleDownload = () => {
    setIsDownloading(true);
    saveDomAsImage({
      exportName,
      domRef,
      domChartRef,
      onCompleteCallback: onClose,
      mobileView
    });
    sendAmplitudeData(DOWNLOAD_CHART);
    sendGAData(DOWNLOAD_CHART);
    const payload: NotificationData = {
      created_at: dayjs(new Date())
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
      id: dayjs(new Date()).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
      is_read: false,
      is_silent: false,
      is_user_message: true,
      message_event: MSG_EVENT.CHART_DOWNLOAD,
      message_text: `Download complete. Please check your Computer's download drive for the PNG file.`,
      updated_at: dayjs(new Date())
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS[000][Z]'),
      message_type: MSG_TYPE.SUCCESS,
      message_data: JSON.stringify({
        chart_id: chartId,
        project: { id: projectId, name: projectName }
      }),
      is_important: false,
      recipient_obj: 0
    };

    dispatch(setUserNotifications(payload));
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        sx={{
          zIndex: 100,
          '& .MuiPaper-root': {
            width: 'fit-content',
            maxWidth: editChartWidth
              ? editChartWidth + 20 + 'px'
              : `${rootWidth - 200}px`,
            position: 'relative',
            overflow: 'visible'
          }
        }}
      >
        <CloseButton onClose={() => onClose()} />
        <DialogContent
          sx={{
            paddingBottom: 0,
            overflowY: 'auto',
            overflowX: 'hidden'
          }}
        >
          <MDBox
            ref={domRef}
            flexDirection='column'
            sx={({ palette }) => ({
              backgroundColor: palette.white.main,
              height: 'fit-content',
              width: 'fit-content',
              paddingX: '10px',
              paddingY: '10px'
            })}
          >
            <MDTypography
              variant='h3'
              color='secondary'
              mb={2}
              mx={1}
              maxWidth='95%'
              sx={{
                overflowWrap: 'break-word',
                whiteSpace: 'pre-wrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                wordBreak: containsWhiteSpace(chartTitle?.trim() || '')
                  ? 'normal'
                  : 'break-all'
              }}
            >
              {chartTitle.trim()}
            </MDTypography>
            {chartDescription && (
              <Grid item mx={1} mb={2}>
                <MDTypography
                  variant='h5'
                  color='info'
                  textAlign='left'
                  sx={{
                    overflowWrap: 'break-word',
                    whiteSpace: 'pre-wrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 10,
                    WebkitBoxOrient: 'vertical'
                  }}
                >
                  {chartDescription.trim() || ''}
                </MDTypography>
              </Grid>
            )}
            {open && getChart}
          </MDBox>
        </DialogContent>

        <DialogActions
          sx={{
            position: 'sticky',
            right: 0,
            bottom: 20,
            borderRadius: '10px',
            justifyContent: 'right',
            mb: 1,
            mx: 1.5,
            py: 1.5
          }}
        >
          <MDBox
            display='flex'
            borderRadius='10px'
            gap={2}
            sx={{ background: 'white' }}
          >
            <MDButton
              color='secondary'
              variant='outlined'
              onClick={onClose}
              circular={false}
            >
              Cancel
            </MDButton>
            <MDButton
              color='primary'
              variant='gradient'
              onClick={handleDownload}
              circular={false}
              disabled={isChartLoading || isDownloading}
            >
              Download
              {isDownloading && (
                <MDBox
                  sx={{
                    marginLeft: '10px',
                    alignSelf: 'center',
                    marginTop: '3px'
                  }}
                >
                  <CircularProgress size={18} style={{ color: 'white' }} />
                </MDBox>
              )}
            </MDButton>
          </MDBox>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DownloadPreviewModal;
